import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { FormikProvider, useFormik } from 'formik';
import { makeStyles } from '@material-ui/styles';
import PropTypes from 'prop-types';
import { useLazyQuery } from '@apollo/client';
import { Typography, Button, message } from 'antd';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { loginContentTypes } from '../login-content';
import { QUERY_CHECK_LANDLORD_EXIST } from '../queries';
import { LOGIN_TYPE } from '~constants/auth';
import event from '~helpers/mixpanel';
import { pageType } from '~constants/pages';
import { FieldInput, FieldCheckbox } from '../login-formik-fields';

const useStyles = makeStyles(
  {
    signupText: {
      marginTop: '12px !important',
      marginBottom: '20px !important',
    },
    input: {
      marginTop: '16px !important',
    },
  },
  { name: 'SignupForEmail' },
);

const SignupForEmail = ({ loginEmail, setSignupValue, handleNextContent, showWarning }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [isDisabled, setIsDisabled] = useState(true);

  const initialValues = useMemo(
    () => ({
      email: loginEmail,
      password: '',
    }),
    [loginEmail],
  );

  const [queryCheckLandlordExist, { data, loading }] = useLazyQuery(QUERY_CHECK_LANDLORD_EXIST, {
    onError: () => {
      message.error(t('sorry_an_error_occurred_please_try_again'));
    },
  });

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        email: yup
          .string()
          .label('Email')
          .email(t('landlord.auth.login.format_error.username'))
          .max(200)
          .required(t('this_part_cannot_be_empty')),
        password: yup
          .string()
          .label('Password')
          .min(8)
          .max(100)
          .required(t('this_part_cannot_be_empty')),
      }),
    [t],
  );

  const queryCheckLandlordExistCompleted = useCallback(
    values => {
      if (!data?.checkLandlordExist?.exist) {
        setSignupValue(values);
        handleNextContent(values?.email, loginContentTypes.FINISH_EMAIL_FOR_SIGNUP);
      }
    },
    [data?.checkLandlordExist?.exist, handleNextContent, setSignupValue],
  );

  const handleClickCreateAccount = async values => {
    event('Clicked Signup Set Password Page', {
      'Clicked Position': 'Continue Btn',
      'Current Page Type': pageType.loginPage,
    });

    try {
      await queryCheckLandlordExist({
        variables: { type: LOGIN_TYPE.EMAIL, value: values?.email },
      });
    } catch (err) {
      message.error(t('sorry_an_error_occurred_please_try_again'));
    } finally {
      queryCheckLandlordExistCompleted(values);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleClickCreateAccount,
  });

  const handleCheckboxChange = ev => {
    formik.setFieldValue('newsletterAccepted', ev.target.checked);

    event('Clicked Signup Set Password Page', {
      'Clicked Position': 'Tickbox for Marketing Info',
      'Current Page Type': pageType.loginPage,
    });
  };

  const handleWarningCheckboxChange = ev => {
    formik.setFieldValue('warningCheckbox', ev.target.checked);
    setIsDisabled(!ev.target.checked);
  };

  const handleInputPasswordFocus = () => {
    event('Clicked Signup Set Password Page', {
      'Clicked Position': 'Password Box',
      'Current Page Type': pageType.loginPage,
    });
  };

  useEffect(() => {
    if (data?.checkLandlordExist?.exist && !formik?.errors?.email) {
      formik.setFieldError('email', t('this_email_already_exists'));
    }
  }, [data?.checkLandlordExist?.exist, formik, t]);

  useEffect(() => {
    event('Viewed Signup Set Password Page', { 'Current Page Type': pageType.loginPage });
  }, []);

  return (
    <>
      <Typography className={classes.signupText}>
        {t('we_will_send_you_an_email_to_verify_your_email_address')}
      </Typography>
      <FormikProvider value={formik} class>
        <FieldInput fieldConfig="email" placeholder={t('email_address')} />
        <FieldInput
          fieldConfig="password"
          type="password"
          placeholder={t('password')}
          className={classes.input}
          onBlur={handleInputPasswordFocus}
        />
        {showWarning && (
          <FieldCheckbox
            fieldConfig="warningCheckbox"
            onChange={handleWarningCheckboxChange}
            label={t('unable_to_serve_students_searching')}
          />
        )}
        <FieldCheckbox
          fieldConfig="newsletterAccepted"
          onChange={handleCheckboxChange}
          label={t(
            'like_to_receive_exclusive_offers_and_the_latest_info_from_student_com_by_email_or_sms',
          )}
        />
        <Button
          loading={loading}
          disabled={showWarning && isDisabled}
          type="primary"
          size="large"
          onClick={formik.handleSubmit}
        >
          {t('create_account')}
        </Button>
      </FormikProvider>
    </>
  );
};

SignupForEmail.propTypes = {
  loginEmail: PropTypes.string.isRequired,
  setSignupValue: PropTypes.func.isRequired,
  handleNextContent: PropTypes.func.isRequired,
};

export default SignupForEmail;
